import { useState } from 'react'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

// local imports
import logo from 'assets/images/koc-logo.svg'
import { PlanTabs } from 'types/PlanTabs'
import { ReservationMeta } from 'types/FloorType'
import { IAllLocationList, ICityList, ICountryList, IDistrictList } from 'types/LocationType'
import useGetAllLocations from './hooks/useGetAllLocations'
import FilterArea from './filter-area/FilterArea'
import MapContainer from './MapContainer'
import ListContainer from './ListContainer'
import FloorSidebar from './FloorSidebar'
import { LoaderWrapper, PageContainer, PageHeader, TextButton } from './styled'

// types - contants etc.
type View = 'map' | 'list'
export interface IMapSearchForm {
  country: ICountryList | undefined
  city: ICityList | undefined
  district: IDistrictList | undefined
  resType: (PlanTabs | undefined)[]
  startDate: Date
  endDate: Date
}

export default function MapPageWrapper() {
  const { t } = useTranslation()
  const location = useLocation()
  const state = location?.state as { response: ReservationMeta }

  /* Floor Sidebar State - Methods  */
  const [selectedLocation, setSelectedLocation] = useState<IAllLocationList | null>(null)
  const handleOpenSidebar = (e: any, location: IAllLocationList) => {
    e.preventDefault()
    setSelectedLocation(location)
  }
  const handleCloseSidebar = () => setSelectedLocation(null)

  /* Toggle Views (Map - List) */
  const [view, setView] = useState<View>('map')
  const toggleView = () => setView((prev) => (prev === 'list' ? 'map' : 'list'))
  const btnLabel = view === 'map' ? t('buttons.list-view') : t('buttons.map-view')
  const btnIcon = view === 'map' ? 'pi pi-align-left' : 'pi pi-map-marker'

  /* Search form default values */
  const defaultValues: IMapSearchForm = {
    country: undefined,
    city: undefined,
    district: undefined,
    resType: [state?.response?.planTab || PlanTabs.OFFICE],
    startDate: state?.response.startDate || new Date(),
    endDate: state?.response.endDate || new Date(),
  }

  /* Search form state  */
  const { watch, resetField, control, setValue } = useForm<IMapSearchForm>({
    defaultValues,
  })
  const country = watch('country')
  const city = watch('city')
  const district = watch('district')
  const resType = watch('resType')
  const startDate = watch('startDate')
  const endDate = watch('endDate')
  const countryLatLng = { lat: country?.latitude || 0, lng: country?.longitude || 0 }
  const cityLatLng = { lat: city?.latitude || 0, lng: city?.longitude || 0 }
  const districtLatLng = { lat: district?.latitude || 0, lng: district?.longitude || 0 }
  /* Search form state  */

  /* Get All Locations */
  const { locationList, isLoading } = useGetAllLocations(resType)

  const headerTitle = () => {
    const locationCount = locationList?.length
    if (!locationCount) return null
    else
      return (
        <>
          {t('map-search.office-count')} {`“${locationCount}”`}
        </>
      )
  }

  return (
    <>
      <PageHeader>
        <div className="header-text filter">{t('map-search.near-offices')}</div>
        <div className="map-header">
          <div className="header-text">{headerTitle()}</div>
          <TextButton onClick={toggleView} icon={btnIcon} label={btnLabel} className="p-button-info p-button-text" />
        </div>
      </PageHeader>

      <PageContainer>
        <FilterArea control={control} resetField={resetField} watch={watch} setValue={setValue} />
        {view === 'map' ? (
          <MapContainer
            openSidebar={handleOpenSidebar}
            locationList={locationList}
            country={countryLatLng}
            city={cityLatLng}
            district={districtLatLng}
          />
        ) : (
          <ListContainer openSidebar={handleOpenSidebar} locationList={locationList} />
        )}
      </PageContainer>

      {/* Floor - Block Sidebar Component */}
      {!!selectedLocation ? (
        <FloorSidebar
          startDate={startDate}
          endDate={endDate}
          handleClose={handleCloseSidebar}
          selectedLocation={selectedLocation}
        />
      ) : null}

      {/* Loader */}
      {isLoading ? (
        <LoaderWrapper className={'loading'}>
          <ProgressSpinner style={{ position: 'absolute', width: '140px', zIndex: 1102 }} />
          <img
            alt="loading-map-search"
            style={{ position: 'absolute', width: '65px', height: '65px', zIndex: 1102 }}
            src={logo}
          />
        </LoaderWrapper>
      ) : null}
    </>
  )
}
